import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ScanFace, Lock, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageMeta } from "@/lib/seo";
import {
  setBiometricUnlockEnabled,
  verifyBiometric,
  markFaceIdAsked,
} from "@/lib/native";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/face-id-intro")({
  head: () => pageMeta({ title: "Enable Face ID — Roava", description: "Lock Roava with Face ID for faster, more private access.", path: "/face-id-intro" }),
  component: FaceIdIntro,
});

function FaceIdIntro() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  async function enable() {
    setBusy(true);
    try {
      await verifyBiometric("Enable Face ID unlock");
      await setBiometricUnlockEnabled(true);
      await markFaceIdAsked();
      toast.success("Face ID enabled");
      nav({ to: "/trip" });
    } catch {
      // user cancelled Apple's sheet — let them retry or skip
      setBusy(false);
    }
  }

  async function skip() {
    await markFaceIdAsked();
    nav({ to: "/trip" });
  }

  return (
    <div className="min-h-dvh bg-background">
      <div
        className="mx-auto max-w-[480px] px-6 flex flex-col min-h-dvh"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)",
        }}
      >
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="size-24 rounded-3xl bg-primary/10 grid place-items-center text-primary">
            <ScanFace className="size-12" strokeWidth={1.5} />
          </div>
          <h1 className="mt-8 text-3xl font-bold tracking-tight">Lock Roava with Face ID</h1>
          <p className="mt-3 text-base text-muted-foreground max-w-sm">
            Add a quick, private layer of protection to your trip memories.
          </p>

          <div className="mt-10 w-full grid gap-4 text-left">
            <Benefit icon={<Lock className="size-5" />} title="Private by default" body="Only your face can open the app — even if your phone is unlocked." />
            <Benefit icon={<Zap className="size-5" />} title="Faster sign-in" body="Skip the password every time you open Roava." />
            <Benefit icon={<ShieldCheck className="size-5" />} title="Your data stays yours" body="Face ID runs on-device. Apple never sends your face anywhere." />
          </div>
        </div>

        <div className="grid gap-3 pt-6">
          <Button onClick={enable} disabled={busy} className="h-14 rounded-2xl text-base font-semibold">
            <ScanFace className="size-5 mr-2" />
            {busy ? "Waiting for Face ID…" : "Enable Face ID"}
          </Button>
          <Button onClick={skip} variant="ghost" className="h-12 rounded-2xl text-muted-foreground">
            Not now
          </Button>
          <p className="text-center text-[11px] text-muted-foreground/70">
            You can change this any time in Profile.
          </p>
        </div>
      </div>
    </div>
  );
}

function Benefit({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="size-10 rounded-xl bg-muted grid place-items-center text-primary shrink-0">{icon}</div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground leading-snug">{body}</div>
      </div>
    </div>
  );
}
